// The script viewer: PDF.js's stock PDFViewer (with its iOS canvas limits), one SVG overlay per
// page for marks. Pencil marks; mouse placement is explicit. Fingers scroll and pinch-zoom,
// and tapping a mark edits it.

import { computed, effect, signal, untracked } from "@preact/signals";
import * as pdfjsLib from "pdfjs-dist";
import workerUrl from "pdfjs-dist/build/pdf.worker.min.mjs?url";
import "pdfjs-dist/web/pdf_viewer.css";
import type { DocIndex } from "../format/derive.ts";
import { displaySize } from "../format/geometry.ts";
import { num } from "../format/serialize.ts";
import type { Anchor, Rect, RegionAnchor } from "../format/types.ts";
import { insideNoteLayout, layoutPage, standbyLabels } from "../layout/layout.ts";
import { svgGroup } from "../layout/svg.ts";
import { GUTTER_GAP, GUTTER_WIDTH, packGutter } from "./gutter.ts";
import { arrangedIds, arranging, toggleArrange } from "./arrange.ts";
import { resizeRegion } from "./anchor-resize.ts";
import { ix, type MarkKind, type MarkRef, panel, selected, view } from "./state.ts";
import { retryScript, scriptLoad } from "./loading.ts";

pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
// pdf_viewer.mjs reads the library from this global while it evaluates, so it can't be a static
// import (static imports evaluate before this module's body sets the global).
(globalThis as { pdfjsLib?: unknown }).pdfjsLib = pdfjsLib;
const { EventBus, PDFLinkService, PDFViewer } = await import("pdfjs-dist/web/pdf_viewer.mjs");

const SVGNS = "http://www.w3.org/2000/svg";
const DRAG_PX = 8;

/** The PDF.js page view parts this module uses. */
interface PageView {
  div: HTMLDivElement;
  viewport: { width: number; height: number; convertToViewportPoint(x: number, y: number): number[]; convertToPdfPoint(x: number, y: number): number[] };
}

export const currentPage = signal(1);
export const pageCount = signal(0);
export const zoom = signal(1);

export interface ViewerHandlers {
  onNew(anchor: Anchor): void;
  onEdit(ref: MarkRef): void;
  onMove(ref: MarkRef, dx: number, dy: number): void;
  onResize(ref: MarkRef, before: RegionAnchor, after: RegionAnchor): void;
  canEdit(): boolean;
}

let viewer: InstanceType<typeof PDFViewer>;
let container: HTMLDivElement;
let viewerHandlers: ViewerHandlers;
let gutterRail: HTMLElement;
let gutterTrack: HTMLElement;
let connections: SVGSVGElement;
let editorLeader: SVGPathElement;
let gutterFrame = 0;
let sharedScrollTop = 0;
const gutterPages = new Map<number, {
  pv: PageView;
  gutter: HTMLElement;
  leaders: SVGGElement;
  items: { link: Rect; card: HTMLElement; line: SVGPathElement }[];
}>();
let fitMode: "page-width" | "page-fit" | null = null;
let applyingFit = false;
/** Retained through deferred PDF renders; explicit user navigation releases it. */
let readingAnchor: { page: number; point: number[]; x: number; y: number } | null = null;
let resizingGutter = false;

function captureReadingAnchor(): typeof readingAnchor {
  if (!viewer?.pdfDocument || view.peek().preview) return null;
  const viewport = container.getBoundingClientRect();
  let candidate: { pv: PageView; page: number } | null = null;
  for (let page = 1; page <= viewer.pagesCount; page++) {
    const pv = pageView(page);
    if (!pv) continue;
    candidate = { pv, page };
    const top = pv.div.getBoundingClientRect().top + pv.div.clientTop;
    // Ignore annotation padding: in a gap, anchor the next actual PDF page.
    if (top + pv.viewport.height >= viewport.top) break;
  }
  if (!candidate) return null;
  const { pv, page } = candidate;
  const box = pv.div.getBoundingClientRect();
  const x = Math.max(0, Math.min(pv.viewport.width, viewport.left - box.left - pv.div.clientLeft));
  const y = Math.max(0, Math.min(pv.viewport.height, viewport.top - box.top - pv.div.clientTop));
  return {
    page, point: pv.viewport.convertToPdfPoint(x, y),
    x: box.left + pv.div.clientLeft + x - viewport.left,
    y: box.top + pv.div.clientTop + y - viewport.top,
  };
}

function restoreReadingAnchor(): void {
  if (!readingAnchor || view.peek().preview) return;
  const pv = pageView(readingAnchor.page);
  if (!pv) return;
  const [x, y] = pv.viewport.convertToViewportPoint(readingAnchor.point[0]!, readingAnchor.point[1]!);
  const box = pv.div.getBoundingClientRect();
  const viewport = container.getBoundingClientRect();
  const dx = box.left + pv.div.clientLeft + x! - viewport.left - readingAnchor.x;
  const dy = box.top + pv.div.clientTop + y! - viewport.top - readingAnchor.y;
  container.scrollLeft += dx;
  container.scrollTop += dy;
  sharedScrollTop = container.scrollTop;
  gutterTrack.style.height = `${container.scrollHeight - container.clientHeight + gutterRail.clientHeight}px`;
  gutterRail.scrollTop = sharedScrollTop;
}

function installGutterResizing(): void {
  const divider = document.getElementById("gutterDivider")!;
  let width = -1;
  let frame = 0;
  let requested = GUTTER_WIDTH;
  let drag: { id: number; x: number; width: number } | null = null;
  const bounds = () => {
    const max = Math.max(0, Math.min(480, document.documentElement.clientWidth - GUTTER_GAP - 160));
    return { min: Math.min(GUTTER_WIDTH, max), max };
  };
  const apply = (value: number) => {
    const { min, max } = bounds();
    const next = Math.round(Math.max(min, Math.min(max, Number.isFinite(value) ? value : GUTTER_WIDTH)));
    divider.setAttribute("aria-valuemin", String(min));
    divider.setAttribute("aria-valuemax", String(max));
    divider.setAttribute("aria-valuenow", String(next));
    divider.setAttribute("aria-valuetext", `${next} pixels`);
    if (next === width) return;
    readingAnchor ??= captureReadingAnchor();
    width = next;
    document.documentElement.style.setProperty("--gutter-width", `${width}px`);
    document.documentElement.style.setProperty("--gutter-space", `${width + GUTTER_GAP}px`);
    resizingGutter = true;
    try {
      if (fitMode) fit(fitMode);
      redrawAll();
      restoreReadingAnchor();
    } finally {
      resizingGutter = false;
    }
    scheduleGutters();
  };
  const flush = () => {
    if (frame) cancelAnimationFrame(frame);
    frame = 0;
    apply(requested);
  };
  const finish = () => {
    if (!drag) return;
    const { id } = drag;
    drag = null;
    flush();
    view.value = { ...view.peek(), gutterWidth: width };
    divider.classList.remove("dragging");
    document.documentElement.classList.remove("gutter-resizing");
    if (divider.hasPointerCapture(id)) divider.releasePointerCapture(id);
  };
  divider.addEventListener("pointerdown", (e) => {
    if (drag || e.button !== 0 || !e.isPrimary) return;
    e.preventDefault();
    divider.focus({ preventScroll: true });
    readingAnchor = captureReadingAnchor();
    requested = width;
    drag = { id: e.pointerId, x: e.clientX, width };
    divider.setPointerCapture(e.pointerId);
    divider.classList.add("dragging");
    document.documentElement.classList.add("gutter-resizing");
  });
  divider.addEventListener("pointermove", (e) => {
    if (!drag || drag.id !== e.pointerId) return;
    requested = drag.width + drag.x - e.clientX;
    if (!frame) frame = requestAnimationFrame(() => { frame = 0; apply(requested); });
  });
  divider.addEventListener("pointerup", (e) => {
    if (drag?.id !== e.pointerId) return;
    requested = drag.width + drag.x - e.clientX;
    finish();
  });
  divider.addEventListener("pointercancel", (e) => { if (drag?.id === e.pointerId) finish(); });
  divider.addEventListener("lostpointercapture", (e) => { if (drag?.id === e.pointerId) finish(); });
  window.addEventListener("blur", finish);
  divider.addEventListener("keydown", (e) => {
    if (drag || e.altKey || e.ctrlKey || e.metaKey) return;
    const { min, max } = bounds();
    const step = e.shiftKey ? 40 : 10;
    const next = e.key === "ArrowLeft" ? width + step : e.key === "ArrowRight" ? width - step
      : e.key === "Home" ? min : e.key === "End" ? max : null;
    if (next === null) return;
    e.preventDefault();
    readingAnchor = captureReadingAnchor();
    apply(next);
    view.value = { ...view.peek(), gutterWidth: width };
  });
  // A late page render may change padding after a drag ends. Keep restoring until
  // the user acts elsewhere, but never fight their next scroll, edit or navigation.
  const releaseAnchor = (e: Event) => {
    if (e.target !== divider) readingAnchor = null;
  };
  document.addEventListener("pointerdown", releaseAnchor, { capture: true });
  document.addEventListener("keydown", releaseAnchor, { capture: true });
  document.addEventListener("wheel", () => { readingAnchor = null; }, { passive: true, capture: true });
  window.addEventListener("resize", () => {
    finish();
    apply(view.peek().gutterWidth);
  });
  apply(view.peek().gutterWidth);
}
/** A drag preview: new region in CSS px, or a moved/resized anchor in PDF space. */
let preview: { page: number; rect?: Rect; ref?: MarkRef; dx?: number; dy?: number; anchor?: RegionAnchor } | null = null;

function scheduleGutters(): void {
  if (gutterFrame) return;
  gutterFrame = requestAnimationFrame(() => {
    gutterFrame = 0;
    restoreReadingAnchor();
    syncGutters();
  });
}

/** One vertical reading position; only the PDF pans horizontally within its reserved area. */
function syncGutters(): void {
  if (view.peek().preview) return;
  gutterTrack.style.height = `${container.scrollHeight - container.clientHeight + gutterRail.clientHeight}px`;
  const viewport = container.getBoundingClientRect();
  const rail = gutterRail.getBoundingClientRect();
  const overlayTop = connections.getBoundingClientRect().top;
  for (const { pv, gutter, leaders, items } of gutterPages.values()) {
    gutter.style.top = `${pv.div.offsetTop + pv.div.clientTop}px`;
    const page = pv.div.getBoundingClientRect();
    const visible = page.bottom >= viewport.top && page.top <= viewport.bottom;
    leaders.style.display = visible ? "" : "none";
    if (!visible) continue;
    for (const { link, card, line } of items) {
      const x = page.left + pv.div.clientLeft + link[2];
      const y = page.top + pv.div.clientTop + link[3] - overlayTop;
      const targetY = card.getBoundingClientRect().top + card.offsetHeight / 2 - overlayTop;
      line.style.display = x < viewport.left || x > viewport.right ? "none" : "";
      line.setAttribute("d", `M ${x} ${y} L ${viewport.right + GUTTER_GAP / 2} ${y} L ${rail.left + 8} ${targetY}`);
    }
  }
  const form = panel.peek();
  editorLeader.style.display = form ? "" : "none";
  if (form) {
    const pv = pageView(form.anchor.page);
    if (!pv) return;
    const r = screenRects(pv.viewport, form.anchor).reduce((first, r) => r[1] < first[1] ? r : first);
    const page = pv.div.getBoundingClientRect();
    const x = page.left + pv.div.clientLeft + r[2];
    const y = page.top + pv.div.clientTop + r[3] - overlayTop;
    const head = document.querySelector(".panel-head")?.getBoundingClientRect();
    editorLeader.style.display = x < viewport.left || x > viewport.right ? "none" : "";
    editorLeader.setAttribute("d", `M ${x} ${y} L ${viewport.right + GUTTER_GAP / 2} ${y} L ${rail.left} ${(head ? head.top + head.height / 2 : rail.top + 24) - overlayTop}`);
  }
}

export async function openViewer(url: string, handlers: ViewerHandlers): Promise<void> {
  container = document.getElementById("viewerContainer") as HTMLDivElement;
  viewerHandlers = handlers;
  gutterRail = document.getElementById("gutterRail")!;
  gutterTrack = document.getElementById("gutterTrack")!;
  // Mouse placement prevents the browser's default focus; focus explicitly without moving the book.
  container.addEventListener("pointerdown", (e) => {
    if (e.pointerType === "mouse" && e.button === 0) container.focus({ preventScroll: true });
  }, { capture: true });
  connections = document.getElementById("gutterConnections") as unknown as SVGSVGElement;
  editorLeader = el("path", { class: "editor-leader gutter-leader selected" }, connections);
  const eventBus = new EventBus();
  const linkService = new PDFLinkService({ eventBus });
  // Not passing maxCanvasPixels or enableDetailCanvas: PDFViewer picks its iOS defaults itself.
  viewer = new PDFViewer({ container, viewer: document.getElementById("viewer") as HTMLDivElement, eventBus, linkService, textLayerMode: 0, annotationMode: 0 });
  linkService.setViewer(viewer);
  (globalThis as { q2qViewer?: unknown }).q2qViewer = viewer;
  installGutterResizing();
  let loadingTask: pdfjsLib.PDFDocumentLoadingTask | undefined;
  let attempt = 0;
  let targetPage = Math.max(1, view.peek().page);
  let requestedScale = view.peek().scale;
  const fail = (error: unknown) => {
    container.inert = true;
    container.setAttribute("aria-busy", "false");
    gutterRail.inert = true;
    scriptLoad.value = { ...scriptLoad.peek(), stage: "error", error: error instanceof Error ? error.message : String(error) };
  };
  const load = async (retry = false) => {
    const generation = ++attempt;
    targetPage = Math.max(1, view.peek().page);
    requestedScale = view.peek().scale;
    scriptLoad.value = { stage: "loading", startedAt: retry ? performance.now() : scriptLoad.peek().startedAt };
    container.inert = true;
    container.setAttribute("aria-busy", "true");
    gutterRail.inert = true;
    pageCount.value = 0;
    currentPage.value = targetPage;
    try {
      // Reuse the viewer and its handlers. Only the failed PDF task is replaced.
      const previous = loadingTask;
      loadingTask = undefined;
      viewer.setDocument(null);
      linkService.setDocument(null);
      gutterPages.clear();
      gutterTrack.replaceChildren();
      connections.replaceChildren(editorLeader);
      if (previous) await previous.destroy();
      if (generation !== attempt) return;
      const task = loadingTask = pdfjsLib.getDocument({
        url,
        disableAutoFetch: true,
        disableStream: true,
        wasmUrl: "/pdfjs/wasm/",
        cMapUrl: "/pdfjs/cmaps/",
        cMapPacked: true,
        standardFontDataUrl: "/pdfjs/standard_fonts/",
        iccUrl: "/pdfjs/iccs/",
      });
      task.onProgress = ({ loaded, total }: { loaded: number; total?: number }) => {
        if (generation !== attempt || scriptLoad.peek().stage !== "loading") return;
        const knownTotal = total && Number.isFinite(total) && total > 0 ? total : undefined;
        scriptLoad.value = {
          ...scriptLoad.peek(),
          stage: knownTotal && loaded >= knownTotal ? "preparing" : "loading",
          loaded: Number.isFinite(loaded) && loaded >= 0 ? (knownTotal ? Math.min(loaded, knownTotal) : loaded) : undefined,
          total: knownTotal,
        };
      };
      const pdf = await task.promise;
      if (generation !== attempt) return;
      targetPage = Math.min(targetPage, pdf.numPages);
      currentPage.value = targetPage;
      pageCount.value = pdf.numPages;
      scriptLoad.value = { ...scriptLoad.peek(), stage: "preparing" };
      // Surface page-fetch failures too: PDFViewer otherwise logs these without an actionable UI.
      await pdf.getPage(targetPage);
      if (generation !== attempt) return;
      viewer.setDocument(pdf);
      linkService.setDocument(pdf, null);
      void viewer.pagesPromise?.catch((error: unknown) => {
        if (generation === attempt) fail(error);
      });
    } catch (error) {
      if (generation === attempt) fail(error);
    }
  };
  retryScript.value = () => { void load(true); };

  eventBus.on("pagesinit", () => {
    const scale = requestedScale;
    if (scale === "page-width" || scale === "page-fit") fit(scale);
    else viewer.currentScaleValue = scale || "1";
    viewer.currentPageNumber = targetPage;
    zoom.value = viewer.currentScale;
  });
  eventBus.on("pagechanging", ({ pageNumber }: { pageNumber: number }) => {
    if (scriptLoad.peek().stage !== "ready" && pageNumber !== targetPage) return;
    currentPage.value = pageNumber;
    view.value = { ...view.value, page: pageNumber };
  });
  eventBus.on("scalechanging", () => {
    zoom.value = viewer.currentScale;
    if (!resizingGutter) readingAnchor = null;
    if (!applyingFit) fitMode = null;
    view.value = { ...view.value, scale: fitMode ?? viewer.currentScaleValue };
    redrawAll();
  });
  // PDF.js clears a page div when it resets it; reattach the overlay whenever a page renders.
  eventBus.on("pagerender", ({ pageNumber, source }: { pageNumber: number; source: PageView }) => {
    if (source === pageView(pageNumber)) drawPage(pageNumber);
  });
  eventBus.on("pagerendered", ({ pageNumber, source, error }: { pageNumber: number; source: PageView; error?: unknown }) => {
    if (source !== pageView(pageNumber)) return;
    if (error) {
      if (pageNumber === (scriptLoad.peek().stage === "ready" ? viewer.currentPageNumber : targetPage)) fail(error);
      return;
    }
    if (pageNumber === targetPage && scriptLoad.peek().stage === "preparing") {
      // Rendering a neighbouring/prefetched page is not readiness for the restored reading page.
      scriptLoad.value = { ...scriptLoad.peek(), stage: "ready" };
      container.inert = false;
      container.setAttribute("aria-busy", "false");
      gutterRail.inert = false;
    }
    drawPage(pageNumber);
  });

  // Only where the open editor's mark is matters to the overlay, not what is being typed in it.
  const panelMark = computed(() => {
    const p = panel.value;
    return p ? JSON.stringify([p.mode, p.id, p.anchor, p.kind === "note" ? [p.style, p.placement, p.text, p.layer] : null]) : "";
  });
  const drawingView = computed(() => JSON.stringify([view.value.hiddenDepartments, view.value.hiddenLayers, view.value.preview]));
  effect(() => {
    // Data, filters and draft note content change the drawing; selection only changes emphasis.
    void ix.value;
    void drawingView.value;
    void panelMark.value;
    untracked(redrawAll);
  });
  effect(() => updateSelection());
  effect(() => {
    document.documentElement.classList.toggle("arrange-mode", arranging.value);
  });
  new ResizeObserver(scheduleGutters).observe(gutterRail);
  const previewMode = computed(() => view.value.preview);
  effect(() => {
    const editing = !previewMode.value;
    container.classList.toggle("editing", editing);
    document.documentElement.classList.toggle("gutter-preview", !editing);
    if (fitMode) untracked(() => fit(fitMode!));
    untracked(redrawAll);
  });
  const editorOpen = computed(() => Boolean(panel.value) && !view.value.preview);
  effect(() => {
    document.documentElement.classList.toggle("gutter-editor-open", editorOpen.value);
    scheduleGutters();
  });
  let previousWidth = container.clientWidth;
  let resizeFrame = 0;
  const resize = new ResizeObserver(() => {
    if (resizeFrame) return;
    resizeFrame = requestAnimationFrame(() => {
      resizeFrame = 0;
      const width = container.clientWidth;
      resizingGutter = Boolean(readingAnchor);
      try {
        if (fitMode && (width !== previousWidth || fitMode === "page-fit")) fit(fitMode);
      } finally {
        resizingGutter = false;
      }
      previousWidth = width;
      scheduleGutters();
    });
  });
  resize.observe(container);
  container.addEventListener("scroll", () => {
    if (!view.peek().preview && container.scrollTop !== sharedScrollTop) {
      sharedScrollTop = container.scrollTop;
      gutterTrack.style.height = `${container.scrollHeight - container.clientHeight + gutterRail.clientHeight}px`;
      gutterRail.scrollTop = sharedScrollTop;
    }
    scheduleGutters();
  }, { passive: true });
  gutterRail.addEventListener("scroll", () => {
    if (view.peek().preview || panel.peek()) return;
    if (gutterRail.scrollTop !== sharedScrollTop) {
      sharedScrollTop = gutterRail.scrollTop;
      container.scrollTop = sharedScrollTop;
    }
    scheduleGutters();
  }, { passive: true });
  const toolbar = document.querySelector(".toolbar")!;
  let banners: Element | null = null;
  const updateTop = () => {
    const current = document.querySelector(".banners");
    if (current !== banners) {
      if (banners) chromeResize.unobserve(banners);
      if (current) chromeResize.observe(current);
      banners = current;
    }
    document.documentElement.style.setProperty("--viewer-top", `${Math.max(toolbar.getBoundingClientRect().bottom, banners?.getBoundingClientRect().bottom ?? 0)}px`);
    scheduleGutters();
  };
  // Banner/filter changes resize the viewer. Apply insets outside observer delivery.
  let chromeFrame = 0;
  const scheduleTop = () => {
    if (chromeFrame) return;
    chromeFrame = requestAnimationFrame(() => {
      chromeFrame = 0;
      updateTop();
    });
  };
  const chromeResize = new ResizeObserver(scheduleTop);
  chromeResize.observe(toolbar);
  new MutationObserver(scheduleTop).observe(document.getElementById("app")!, { childList: true });
  updateTop();
  const updateKeyboard = () => {
    const viewport = window.visualViewport;
    document.documentElement.style.setProperty("--keyboard-inset", `${viewport ? Math.max(0, innerHeight - viewport.height - viewport.offsetTop) : 0}px`);
  };
  window.visualViewport?.addEventListener("resize", updateKeyboard);
  window.visualViewport?.addEventListener("scroll", updateKeyboard);
  updateKeyboard();

  installGestures(handlers);

  // WebKit keeps canvas memory alive across bfcache; release it when the page goes away.
  addEventListener("pagehide", () => {
    for (const c of document.querySelectorAll("canvas")) {
      c.width = 0;
      c.height = 0;
    }
  });
  addEventListener("pageshow", (e) => {
    if (e.persisted) location.reload();
  });
  await load();
}

export function goToPage(n: number): void {
  readingAnchor = null;
  if (viewer) viewer.currentPageNumber = Math.min(Math.max(1, n), pageCount.value);
}

/** Keyboard/dial scrolling uses the same container and gutter synchronization as wheel scrolling. */
export function scrollViewer(key: string): void {
  readingAnchor = null;
  switch (key) {
    case "ArrowUp": container.scrollTop -= 40; break;
    case "ArrowDown": container.scrollTop += 40; break;
    case "ArrowLeft": container.scrollLeft -= 40; break;
    case "ArrowRight": container.scrollLeft += 40; break;
    case "PageUp": container.scrollTop -= Math.max(40, container.clientHeight * 0.9); break;
    case "PageDown": container.scrollTop += Math.max(40, container.clientHeight * 0.9); break;
  }
}
export function zoomBy(steps: number): void {
  if (steps > 0) viewer.increaseScale();
  else viewer.decreaseScale();
}
export function fit(mode: "page-width" | "page-fit"): void {
  if (!viewer?.pdfDocument) return;
  fitMode = mode;
  const pv = pageView(viewer.currentPageNumber);
  if (!pv) return;
  const unitWidth = pv.viewport.width / viewer.currentScale;
  const unitHeight = pv.viewport.height / viewer.currentScale;
  const width = Math.max(80, container.clientWidth - 40);
  const scale = mode === "page-fit" ? Math.min(width / unitWidth, (container.clientHeight - 5) / unitHeight) : width / unitWidth;
  applyingFit = true;
  try {
    viewer.currentScale = scale;
    view.value = { ...view.value, scale: mode };
  } finally {
    applyingFit = false;
  }
}

/** Scroll a mark into view and select it. */
export function reveal(ref: MarkRef, page: number): void {
  readingAnchor = null;
  selected.value = ref;
  const index = ix.value;
  const anchor = ref.kind === "moment" ? index?.moments.get(ref.id)?.anchor
    : ref.kind === "note" ? index?.doc.notes.find((n) => n.id === ref.id)?.anchor
    : index?.doc.standbys.find((s) => s.id === ref.id)?.anchor;
  const pv = pageView(page);
  if (!anchor || !pv) return;
  const r = screenRects(pv.viewport, anchor)[0]!;
  const [x, y] = pv.viewport.convertToPdfPoint((r[0] + r[2]) / 2, (r[1] + r[3]) / 2);
  viewer.scrollPageIntoView({ pageNumber: page, destArray: [null, { name: "XYZ" }, x, y, null], ignoreDestinationZoom: true, center: "both" });
}

// ---------------------------------------------------------------- drawing

function el<K extends keyof SVGElementTagNameMap>(name: K, attrs: Record<string, string | number>, parent?: Element): SVGElementTagNameMap[K] {
  const e = document.createElementNS(SVGNS, name);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  parent?.append(e);
  return e;
}

function pageView(n: number): PageView | null {
  return (viewer?.getPageView(n - 1) as PageView | undefined) ?? null;
}

function markAnchor(ref: MarkRef): Anchor | undefined {
  const index = ix.value;
  return ref.kind === "moment" ? index?.moments.get(ref.id)?.anchor
    : ref.kind === "note" ? index?.doc.notes.find((n) => n.id === ref.id)?.anchor
    : index?.doc.standbys.find((s) => s.id === ref.id)?.anchor;
}

/** Normalized CSS-pixel rects of an anchor on its page view. */
function screenRects(vp: PageView["viewport"], anchor: Anchor): Rect[] {
  if (anchor.shape === "pin") {
    const [x, y] = vp.convertToViewportPoint(anchor.point[0], anchor.point[1]) as [number, number];
    return [[x, y, x, y]];
  }
  return anchor.rects.map((r) => {
    const a = vp.convertToViewportPoint(r[0], r[1]) as [number, number];
    const b = vp.convertToViewportPoint(r[2], r[3]) as [number, number];
    return [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.max(a[0], b[0]), Math.max(a[1], b[1])];
  });
}

export function shifted(anchor: Anchor, dx: number, dy: number): Anchor {
  return anchor.shape === "pin"
    ? { ...anchor, point: [num(anchor.point[0] + dx), num(anchor.point[1] + dy)] }
    : { ...anchor, rects: anchor.rects.map((r) => [num(r[0] + dx), num(r[1] + dy), num(r[2] + dx), num(r[3] + dy)] as Rect) };
}

export function redrawAll(): void {
  if (!viewer?.pdfDocument) return;
  for (let i = 1; i <= viewer.pagesCount; i++) drawPage(i);
  scheduleGutters();
}

function hatchPattern(defs: SVGDefsElement, id: string, color: string): string {
  const p = el("pattern", { id, width: 6, height: 6, patternUnits: "userSpaceOnUse", patternTransform: "rotate(45)" }, defs);
  el("line", { x1: 0, y1: 0, x2: 0, y2: 6, stroke: color, "stroke-width": 1.5, "stroke-opacity": 0.7 }, p);
  return `url(#${id})`;
}

interface LabelPart {
  text: string;
  /** Department or layer colour: drawn as a bar before the text, never as the text colour. */
  color: string;
  detail?: string;
  italic?: boolean;
}

interface GutterLabel {
  group: SVGGElement;
  parts: LabelPart[];
  box: Rect;
  link: Rect;
  variant: "plain" | "standby" | "alert";
}

/** Selection never repacks labels or steals focus from the selected button. */
function updateSelection(root: ParentNode = document): void {
  const ref = selected.value;
  const multiple = arranging.value;
  const ids = arrangedIds.value;
  for (const node of root.querySelectorAll<SVGElement | HTMLElement>(".mark, .gutter-label, .gutter-leader:not(.editor-leader)")) {
    const kind = node.dataset.mark ?? node.dataset.kind;
    const match = multiple ? kind === "moment" && ids.has(node.dataset.id!) : node.dataset.id === ref?.id && kind === ref?.kind;
    node.classList.toggle("selected", match);
    const button = node.querySelector<HTMLButtonElement>(".gutter-select");
    if (button) {
      button.setAttribute("aria-pressed", String(match));
      button.disabled = multiple && kind !== "moment";
    }
  }
  for (const gutter of root.querySelectorAll<HTMLElement>(".q2q-gutter")) {
    const active = Boolean(gutter.querySelector(".gutter-label.selected"));
    gutter.classList.toggle("has-selection", active);
    gutter.querySelector<HTMLButtonElement>(".gutter-edit")!.disabled = multiple || !active || Boolean(panel.value) || !viewerHandlers.canEdit();
  }
  updateResizeHandles();
}

function updateResizeHint(handle: SVGGElement, pointerType = handle.dataset.pointerType ?? "mouse"): void {
  handle.dataset.pointerType = pointerType;
  const mouseHint = pointerType === "mouse" && view.value.mouseMode === "interact";
  handle.classList.toggle("mouse-resize-hint", mouseHint);
  handle.querySelector("title")!.textContent = mouseHint ? "Choose the pencil tool to resize" : "Drag to resize this rectangle";
}

/** Separate controls keep selection changes from rebuilding/repacking the gutter. */
function updateResizeHandles(): void {
  const existing = document.querySelector<SVGGElement>(".resize-controls");
  const ref = selected.value;
  if (!ref || arranging.value || !viewerHandlers.canEdit()) return existing?.remove();
  const saved = markAnchor(ref);
  const anchor = preview?.ref?.kind === ref.kind && preview.ref.id === ref.id && saved
    ? preview.anchor ?? shifted(saved, preview.dx ?? 0, preview.dy ?? 0)
    : saved;
  if (anchor?.shape !== "region") return existing?.remove();
  const pv = pageView(anchor.page);
  const svg = pv?.div.querySelector<SVGSVGElement>(":scope > svg.q2q-overlay");
  // A filtered-out mark must not leave invisible, active geometry controls.
  if (!pv || !svg || ![...svg.querySelectorAll<SVGGElement>(".mark")].some((g) => g.dataset.id === ref.id && g.dataset.mark === ref.kind)) return existing?.remove();
  let controls = existing;
  if (!controls || controls.parentNode !== svg || controls.dataset.mark !== ref.kind || controls.dataset.id !== ref.id || controls.childElementCount !== anchor.rects.length * 4) {
    controls?.remove();
    controls = el("g", { class: "resize-controls", "data-mark": ref.kind, "data-id": ref.id }, svg);
  }
  // Safari's Pencil Touch.target must stay connected, including during selection feedback.
  for (const [i, r] of screenRects(pv.viewport, anchor).entries()) {
    for (const [corner, x, y] of [["nw", r[0], r[1]], ["ne", r[2], r[1]], ["se", r[2], r[3]], ["sw", r[0], r[3]]] as const) {
      let handle = controls.querySelector<SVGGElement>(`.resize-target[data-corner="${corner}"][data-rect-index="${i}"]`);
      if (!handle) {
        handle = el("g", { class: "resize-target", "data-corner": corner, "data-rect-index": i }, controls);
        el("title", {}, handle);
        const updateHint = (event: PointerEvent) => updateResizeHint(event.currentTarget as SVGGElement, event.pointerType);
        handle.addEventListener("pointerenter", updateHint);
        handle.addEventListener("pointerdown", updateHint);
        el("circle", { class: "resize-hit", r: 14 }, handle);
        el("rect", { class: "resize-handle", "data-corner": corner, "data-rect-index": i, width: 10, height: 10, rx: 1 }, handle);
      }
      updateResizeHint(handle);
      const hit = handle.querySelector("circle")!;
      hit.setAttribute("cx", String(x));
      hit.setAttribute("cy", String(y));
      const visible = handle.querySelector("rect")!;
      visible.setAttribute("x", String(x - 5));
      visible.setAttribute("y", String(y - 5));
    }
  }
}

function drawGutter(pv: PageView, labels: GutterLabel[], page: number): void {
  const gutter = document.createElement("aside");
  gutter.className = "q2q-gutter";
  gutter.setAttribute("aria-label", `Annotations on p. ${ix.value?.page(page).label || page}`);
  gutter.dataset.pageNumber = String(page);
  const actions = document.createElement("div");
  actions.className = "gutter-actions";
  const hint = document.createElement("span");
  hint.textContent = "Select a label";
  actions.append(hint);
  for (const [text, cls, run] of [
    ["Edit", "gutter-edit primary", (ref: MarkRef) => viewerHandlers.onEdit(ref)],
    ["Show on page", "gutter-reveal", (ref: MarkRef) => reveal(ref, page)],
  ] as const) {
    const button = document.createElement("button");
    button.type = "button";
    button.className = cls;
    button.textContent = text;
    button.addEventListener("click", () => {
      const ref = selected.value;
      if (ref && gutter.querySelector(".gutter-label.selected")) run(ref);
    });
    actions.append(button);
  }
  gutter.append(actions);
  gutterTrack.append(gutter);
  labels.sort((a, b) => a.box[1] - b.box[1] || a.box[0] - b.box[0] || a.group.dataset.id!.localeCompare(b.group.dataset.id!));
  const cards: HTMLElement[] = [];
  for (const item of labels) {
    const card = document.createElement("div");
    card.className = `gutter-label ${item.variant}`;
    card.dataset.mark = item.group.dataset.mark;
    card.dataset.id = item.group.dataset.id;
    const button = document.createElement("button");
    button.type = "button";
    button.className = "gutter-select";
    button.addEventListener("click", () => {
      if (panel.value) return;
      const ref = { kind: item.group.dataset.mark as MarkKind, id: item.group.dataset.id! };
      if (arranging.value) toggleArrange(ref);
      else selected.value = ref;
    });
    for (const part of item.parts) {
      const row = document.createElement("span");
      row.className = `gutter-part${part.italic ? " note" : ""}`;
      row.style.setProperty("--label-color", part.color);
      const title = document.createElement("strong");
      title.textContent = part.text;
      row.append(title);
      if (part.detail) {
        const detail = document.createElement("span");
        detail.className = "gutter-detail";
        detail.textContent = part.detail;
        row.append(detail);
      }
      button.append(row);
    }
    card.append(button);
    gutter.append(card);
    cards.push(card);
  }
  const header = 60;
  const packed = packGutter(cards.map((card, i) => ({ top: labels[i]!.box[1] - header, height: card.getBoundingClientRect().height })), Math.max(0, pv.viewport.height - header));
  const height = header + packed.height;
  gutter.style.height = `${height}px`;
  pv.div.style.paddingBottom = `${Math.max(0, height - pv.viewport.height)}px`;
  const leaders = el("g", { "data-page-number": page }, connections);
  const items: { link: Rect; card: HTMLElement; line: SVGPathElement }[] = [];
  gutterPages.set(page, { pv, gutter, leaders, items });
  for (const [i, card] of cards.entries()) {
    const item = labels[i]!;
    const top = header + packed.tops[i]!;
    card.style.top = `${top}px`;
    const line = el("path", {
      class: "gutter-leader", "data-kind": item.group.dataset.mark!, "data-id": item.group.dataset.id!,
      d: "",
    }, leaders);
    items.push({ link: item.link, card, line });
    const hover = (active: boolean) => {
      card.classList.toggle("hovered", active);
      item.group.classList.toggle("hovered", active);
      line.classList.toggle("hovered", active);
    };
    for (const node of [card, item.group]) {
      node.addEventListener("pointerenter", (e) => { if ((e as PointerEvent).pointerType === "mouse") hover(true); });
      node.addEventListener("pointerleave", () => hover(false));
    }
  }
  updateSelection();
  scheduleGutters();
}

/** The departments and layers this device shows, as layout filters (null: all). */
function shownFilters(index: DocIndex): { departments: Set<string> | null; layers: Set<string> | null } {
  const hd = new Set(view.value.hiddenDepartments);
  const hl = new Set(view.value.hiddenLayers);
  return {
    departments: index.sortedDepartments.some((d) => hd.has(d.id)) ? new Set(index.sortedDepartments.filter((d) => !hd.has(d.id)).map((d) => d.id)) : null,
    layers: index.sortedLayers.some((l) => hl.has(l.id)) ? new Set(index.sortedLayers.filter((l) => !hl.has(l.id)).map((l) => l.id)) : null,
  };
}

let standbyCache: { index: DocIndex; key: string; labels: Map<string, string | null> } | null = null;

/** Standby texts for the shown departments, computed once per data version and filter. */
function cachedStandbyLabels(index: DocIndex, departments: ReadonlySet<string> | null): Map<string, string | null> {
  const key = departments ? JSON.stringify([...departments]) : "*";
  if (standbyCache?.index !== index || standbyCache.key !== key) standbyCache = { index, key, labels: standbyLabels(index, departments) };
  return standbyCache.labels;
}

/** Preview: the page exactly as the prompt book export lays it out, scaled onto the rendered page. */
function drawPreview(svg: SVGGElement, n: number, cssWidth: number, index: DocIndex): void {
  const filters = shownFilters(index);
  const layout = layoutPage(index, n, { ...filters, stamp: null }, cachedStandbyLabels(index, filters.departments));
  const g = el("g", { transform: `scale(${cssWidth / layout.width})` }, svg);
  g.innerHTML = svgGroup(layout);
}

function drawPage(n: number): void {
  const pv = pageView(n);
  const index = ix.value;
  if (!pv?.div || !index) return;
  let svg = pv.div.querySelector<SVGSVGElement>(":scope > svg.q2q-overlay");
  const previous = gutterPages.get(n);
  previous?.gutter.remove();
  previous?.leaders.remove();
  gutterPages.delete(n);
  scheduleGutters();
  const open = panel.value;
  const busy =
    index.doc.moments.some((m) => m.anchor.page === n) ||
    index.doc.standbys.some((s) => s.anchor.page === n) ||
    index.doc.notes.some((x) => x.anchor.page === n) ||
    preview?.page === n ||
    open?.anchor.page === n;
  if (!busy) {
    svg?.remove();
    pv.div.style.paddingBottom = "";
    return;
  }
  if (!svg) {
    svg = el("svg", { class: "q2q-overlay" });
    pv.div.append(svg);
  }
  // Redraw marks without detaching the active Pencil/touch handle in its sibling controls.
  let drawing = svg.querySelector<SVGGElement>(":scope > .page-marks");
  if (!drawing) drawing = el("g", { class: "page-marks" }, svg);
  drawing.replaceChildren();
  const vp = pv.viewport;
  svg.style.width = `${vp.width}px`;
  svg.style.height = `${vp.height}px`;
  if (view.value.preview) {
    svg.querySelector(".resize-controls")?.remove();
    pv.div.style.paddingBottom = "";
    return drawPreview(drawing, n, vp.width, index);
  }
  const defs = el("defs", {}, drawing);
  const labels: GutterLabel[] = [];
  const label = (group: SVGGElement, parts: LabelPart[], box: Rect, rects: Rect[], variant: GutterLabel["variant"] = "plain") => {
    const link = rects.reduce((first, r) => r[1] < first[1] ? r : first);
    labels.push({ group, parts, box, link, variant });
  };
  const hiddenDepts = new Set(view.value.hiddenDepartments);
  const hiddenLayers = new Set(view.value.hiddenLayers);
  const visibleDepts = new Set(index.sortedDepartments.filter((d) => !hiddenDepts.has(d.id)).map((d) => d.id));
  const sel = selected.value;
  const moved = (kind: MarkKind, id: string, a: Anchor): Anchor => {
    if (preview?.ref && preview.ref.kind === kind && preview.ref.id === id) return preview.anchor ?? shifted(a, preview.dx ?? 0, preview.dy ?? 0);
    if (open && open.mode === "edit" && open.id === id) return open.anchor;
    return a;
  };
  const group = (kind: MarkKind, id: string) => {
    const g = el("g", { class: `mark ${kind}`, "data-mark": kind, "data-id": id }, drawing);
    if (sel?.kind === kind && sel.id === id) g.classList.add("selected");
    return g;
  };
  const hit = (g: SVGGElement, b: Rect, pad: number) =>
    el("rect", { class: "hit", x: b[0] - pad, y: b[1] - pad, width: b[2] - b[0] + 2 * pad, height: b[3] - b[1] + 2 * pad }, g);
  const outline = (g: SVGGElement, b: Rect) => {
    el("rect", { class: "sel", x: b[0] - 5, y: b[1] - 5, width: b[2] - b[0] + 10, height: b[3] - b[1] + 10, rx: 4 }, g);
  };
  const bounds = (rs: Rect[]): Rect => [Math.min(...rs.map((r) => r[0])), Math.min(...rs.map((r) => r[1])), Math.max(...rs.map((r) => r[2])), Math.max(...rs.map((r) => r[3]))];
  const drawInside = (g: SVGGElement, anchor: Anchor, text: string, color: string): string | null => {
    const layout = insideNoteLayout(index.page(n), anchor, text, color);
    if (!layout.error) {
      const ink = el("g", { transform: `scale(${vp.width / displaySize(index.page(n))[0]})` }, g);
      ink.innerHTML = svgGroup(layout);
    }
    return layout.error;
  };

  // Notes, under the cue marks.
  for (const note of index.doc.notes) {
    if (note.anchor.page !== n || hiddenLayers.has(note.layer)) continue;
    const draft = open?.mode === "edit" && open.kind === "note" && open.id === note.id ? open : null;
    const content = draft ?? note;
    const layer = index.layers.get(content.layer ?? "");
    const color = layer?.color ?? "#64748b";
    const g = group("note", note.id);
    const anchor = moved("note", note.id, note.anchor);
    const rs = screenRects(vp, anchor);
    const inside = content.placement === "inside";
    const error = inside ? drawInside(g, anchor, content.text, color) : null;
    if (note.anchor.shape === "pin") {
      const [x, y] = rs[0] as Rect;
      el("path", { d: `M${x} ${y} l-7 -14 h14 z`, fill: color, "fill-opacity": 0.85 }, g);
      rs[0] = [x - 7, y - 14, x + 7, y];
    } else if (!inside || error) {
      const fill = content.style === "strike" ? hatchPattern(defs, `h-${note.id}`, color) : "none";
      for (const r of rs) {
        el("rect", { x: r[0], y: r[1], width: r[2] - r[0], height: r[3] - r[1], fill, stroke: color, "stroke-width": 1.5, "stroke-dasharray": content.style === "strike" ? "none" : "5 3" }, g);
      }
    }
    const b = bounds(rs);
    hit(g, b, 6);
    outline(g, b);
    label(g, [{ text: layer?.name ?? "Note", detail: error ? `${error}\n${content.text}` : content.text, color, italic: true }], b, rs);
  }
  if (open?.mode === "new" && open.kind === "note" && open.placement === "inside" && open.anchor.page === n) {
    const color = index.layers.get(open.layer ?? "")?.color ?? "#dc2626";
    drawInside(drawing, open.anchor, open.text, color);
  }

  // Moments with their cues (only the shown departments).
  for (const m of index.doc.moments) {
    if (m.anchor.page !== n) continue;
    const cues = index.cuesOf(m.id).filter((c) => visibleDepts.has(c.department) || !index.departments.has(c.department));
    if (!cues.length && index.cuesOf(m.id).length) continue;
    const colors = cues.map((c) => index.departments.get(c.department)?.color ?? "#888888");
    const g = group("moment", m.id);
    const rs = screenRects(vp, moved("moment", m.id, m.anchor));
    if (m.anchor.shape === "pin") {
      const [x, y] = rs[0] as Rect;
      el("circle", { cx: x, cy: y, r: 9, fill: colors[0] ?? "#888", "fill-opacity": 0.3 }, g);
      colors.forEach((c, i) => el("circle", { cx: x, cy: y, r: 9 + i * 4, fill: "none", stroke: c, "stroke-width": 2.5 }, g));
      el("circle", { cx: x, cy: y, r: 2, fill: "#111" }, g);
      const r = 9 + colors.length * 4;
      rs[0] = [x - r, y - r, x + r, y + r];
    } else {
      for (const r of rs) {
        el("rect", { x: r[0], y: r[1], width: r[2] - r[0], height: r[3] - r[1], fill: colors[0] ?? "#888", "fill-opacity": 0.18 }, g);
        colors.forEach((c, i) => {
          const inset = i * 3;
          el("rect", { x: r[0] + inset, y: r[1] + inset, width: Math.max(1, r[2] - r[0] - 2 * inset), height: Math.max(1, r[3] - r[1] - 2 * inset), fill: "none", stroke: c, "stroke-width": 2 }, g);
        });
      }
    }
    const b = bounds(rs);
    hit(g, b, m.anchor.shape === "pin" ? 14 : 8);
    outline(g, b);
    const parts = cues.map((c) => ({ text: index.cueRef(c), detail: c.label, color: index.departments.get(c.department)?.color ?? "#333" }));
    label(g, parts.length ? parts : [{ text: "(no cues)", color: "#b91c1c" }], b, rs);
  }

  // Standbys, every one and with its full text: in edit mode nothing may hide a mark you can't
  // otherwise reach. (Department filters shorten standby text only in Preview and exports.)
  {
    const texts = cachedStandbyLabels(index, null);
    for (const s of index.doc.standbys) {
      if (s.anchor.page !== n) continue;
      const text = texts.get(s.id) ?? "SB —";
      const g = group("standby", s.id);
      const rs = screenRects(vp, moved("standby", s.id, s.anchor));
      if (s.anchor.shape === "pin") {
        const [x, y] = rs[0] as Rect;
        el("path", { d: `M${x} ${y - 9} l9 9 l-9 9 l-9 -9 z`, fill: "#111", "fill-opacity": 0.85 }, g);
        rs[0] = [x - 9, y - 9, x + 9, y + 9];
      } else {
        for (const r of rs) el("rect", { x: r[0], y: r[1], width: r[2] - r[0], height: r[3] - r[1], fill: "none", stroke: "#111", "stroke-width": 2, "stroke-dasharray": "2 3" }, g);
      }
      const b = bounds(rs);
      hit(g, b, 12);
      outline(g, b);
      label(g, [{ text: text === "SB —" ? "SB — nothing after it yet" : text, color: "#111" }], b, rs, text === "SB —" ? "alert" : "standby");
    }
  }

  // What's being drawn or placed right now.
  if (preview?.rect && preview.page === n) {
    const [x0, y0, x1, y1] = preview.rect;
    el("rect", { class: "preview", x: Math.min(x0, x1), y: Math.min(y0, y1), width: Math.abs(x1 - x0), height: Math.abs(y1 - y0) }, drawing);
  }
  if (open && open.mode === "new" && open.anchor.page === n) {
    for (const r of screenRects(vp, open.anchor)) {
      if (open.anchor.shape === "pin") el("circle", { class: "preview", cx: r[0], cy: r[1], r: 12 }, drawing);
      else el("rect", { class: "preview", x: r[0], y: r[1], width: r[2] - r[0], height: r[3] - r[1] }, drawing);
    }
  }
  drawGutter(pv, labels, n);
}

// ---------------------------------------------------------------- gestures

function installGestures(h: ViewerHandlers): void {
  type Resize = { before: RegionAnchor; rectIndex: number; xSide: 0 | 2; ySide: 1 | 3; bounds: Rect };
  let gesture: { pointerId: number; pointerType: string; placing: boolean; selecting: boolean; page: number; div: HTMLElement; x: number; y: number; hit: MarkRef | null; dragging: boolean; resize: Resize | null } | null = null;
  let pinch: { startDist: number; startScale: number; cx: number; cy: number } | null = null;
  /** After a pen or mouse gesture, its own click is ignored (the pointer handlers dealt with it). */
  let suppressClickUntil = 0;
  /** After a pinch, the lifted fingers mustn't count as a tap. */
  let pinchEndedAt = 0;
  /**
   * The type of the pointer that last went down. WebKit reports a finger tap's click as
   * pointerType "mouse", so the click's own pointerType can't tell a tap from a mouse click.
   */
  let lastDownType = "";

  const pageAt = (target: EventTarget | null) => {
    const div = (target as Element | null)?.closest?.<HTMLElement>(".page");
    const n = Number(div?.dataset.pageNumber);
    return div && n ? { n, div } : null;
  };
  const local = (e: { clientX: number; clientY: number }, div: HTMLElement): [number, number] => {
    const r = div.getBoundingClientRect();
    return [e.clientX - r.left - div.clientLeft, e.clientY - r.top - div.clientTop];
  };
  const markAt = (target: EventTarget | null): MarkRef | null => {
    const g = (target as Element | null)?.closest?.("[data-mark]");
    return g ? { kind: g.getAttribute("data-mark") as MarkKind, id: g.getAttribute("data-id") as string } : null;
  };
  const resizeAt = (target: EventTarget | null, ref: MarkRef | null, x: number, y: number, vp: PageView["viewport"]): Resize | null => {
    const controls = (target as Element | null)?.closest?.(".resize-controls");
    const anchor = ref && markAnchor(ref);
    if (!controls || anchor?.shape !== "region") return null;
    let nearest: SVGRectElement | null = null;
    let distance = 14 * 14 + 0.001;
    // The DOM's stacking order must not decide between overlapping thin-box targets.
    for (const handle of controls.querySelectorAll<SVGRectElement>(".resize-handle")) {
      const hx = Number(handle.getAttribute("x")) + 5;
      const hy = Number(handle.getAttribute("y")) + 5;
      const d = (x - hx) ** 2 + (y - hy) ** 2;
      if (d < distance) {
        distance = d;
        nearest = handle;
      }
    }
    if (!nearest) return null;
    const rectIndex = Number(nearest.dataset.rectIndex);
    const rect = anchor.rects[rectIndex]!;
    const [px, py] = vp.convertToPdfPoint(Number(nearest.getAttribute("x")) + 5, Number(nearest.getAttribute("y")) + 5);
    return {
      before: anchor, rectIndex,
      xSide: Math.abs(px! - rect[0]) < Math.abs(px! - rect[2]) ? 0 : 2,
      ySide: Math.abs(py! - rect[1]) < Math.abs(py! - rect[3]) ? 1 : 3,
      bounds: ix.value!.page(anchor.page).view,
    };
  };
  const resized = (g: NonNullable<typeof gesture>, vp: PageView["viewport"], x: number, y: number): RegionAnchor => {
    const r = g.resize!;
    const a = vp.convertToPdfPoint(g.x, g.y);
    const b = vp.convertToPdfPoint(x, y);
    return resizeRegion(r.before, r.rectIndex, r.xSide, r.ySide, b[0]! - a[0]!, b[1]! - a[1]!, r.bounds);
  };
  const cancel = () => {
    if (!gesture) return;
    const { page: p, pointerId } = gesture;
    gesture = null;
    preview = null;
    suppressClickUntil = performance.now() + 400;
    if (container.hasPointerCapture(pointerId)) container.releasePointerCapture(pointerId);
    drawPage(p);
  };

  const mouseMode = computed(() => view.value.mouseMode);
  effect(() => {
    void mouseMode.value;
    // Invalidate mouse and existing-mark gestures; blank-page Pencil placement ignores mouse tools.
    if (gesture && (gesture.pointerType === "mouse" || gesture.hit || gesture.resize)) untracked(cancel);
  });
  effect(() => {
    void arranging.value;
    untracked(cancel);
  });
  const resizeContext = computed(() => {
    const ref = selected.value;
    return JSON.stringify([h.canEdit(), zoom.value, view.value.hiddenDepartments, view.value.hiddenLayers, ref, ref && markAnchor(ref)]);
  });
  effect(() => {
    void resizeContext.value;
    // A resize uses the original anchor and viewport; never commit it after either changes.
    if (gesture?.resize) untracked(cancel);
  });
  window.addEventListener("blur", cancel);
  window.addEventListener("resize", cancel);
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && gesture) {
      e.preventDefault();
      cancel();
    }
  });

  container.addEventListener("pointerdown", (e) => (lastDownType = e.pointerType), { capture: true });
  container.addEventListener("pointerdown", (e) => {
    if ((e.target as Element).closest(".q2q-gutter")) return;
    // Touch never places; mouse gestures snapshot their permissions at the initial press.
    if (gesture || e.button > 0 || pinch || (e.pointerType !== "pen" && e.pointerType !== "mouse")) return;
    const p = pageAt(e.target);
    if (!p || !h.canEdit()) return;
    const [x, y] = local(e, p.div);
    const vp = pageView(p.n)?.viewport;
    if (!vp) return;
    const hit = markAt(e.target);
    const selecting = arranging.value;
    const resize = selecting ? null : resizeAt(e.target, hit, x, y, vp);
    if (!resize && (x < 0 || y < 0 || x > vp.width || y > vp.height)) return;
    if (resize && hit) selected.value = { ...hit }; // Keep a grabbed handle selected beyond save-feedback expiry.
    e.preventDefault();
    try {
      container.setPointerCapture(e.pointerId);
    } catch {
      // capture is best-effort
    }
    gesture = { pointerId: e.pointerId, pointerType: e.pointerType, placing: !selecting && (e.pointerType === "pen" || mouseMode.value === "place"), selecting, page: p.n, div: p.div, x, y, hit, dragging: false, resize };
  });

  container.addEventListener("pointermove", (e) => {
    const g = gesture;
    if (!g || e.pointerId !== g.pointerId) return;
    e.preventDefault();
    const pv = pageView(g.page);
    if (!pv) return;
    let [x, y] = local(e, g.div);
    if (!g.resize) {
      x = Math.max(0, Math.min(pv.viewport.width, x));
      y = Math.max(0, Math.min(pv.viewport.height, y));
    }
    if (!g.dragging && Math.hypot(x - g.x, y - g.y) < DRAG_PX) return;
    g.dragging = true;
    if (!g.placing) return;
    if (g.resize) {
      preview = { page: g.page, ref: g.hit!, anchor: resized(g, pv.viewport, x, y) };
    } else if (g.hit) {
      const a = pv.viewport.convertToPdfPoint(g.x, g.y) as [number, number];
      const b = pv.viewport.convertToPdfPoint(x, y) as [number, number];
      preview = { page: g.page, ref: g.hit, dx: b[0] - a[0], dy: b[1] - a[1] };
    } else {
      preview = { page: g.page, rect: [g.x, g.y, x, y] };
    }
    drawPage(g.page);
  });

  container.addEventListener("pointerup", (e) => {
    const g = gesture;
    if (!g || e.pointerId !== g.pointerId) return;
    gesture = null;
    suppressClickUntil = performance.now() + 400;
    const pv = pageView(g.page);
    const pre = preview;
    preview = null;
    if (!pv) return;
    const vp = pv.viewport;
    if (!h.canEdit() || g.selecting !== arranging.value) return drawPage(g.page);
    if (g.selecting) {
      if (!g.dragging && g.hit) toggleArrange(g.hit);
      return;
    }
    if (g.resize) {
      drawPage(g.page);
      // A handle tap is a no-op, never an accidental trip into the text editor.
      if (g.dragging && g.placing) {
        const [x, y] = local(e, g.div);
        h.onResize(g.hit!, g.resize.before, resized(g, vp, x, y));
      }
      return;
    }
    if (!g.dragging) {
      drawPage(g.page);
      if (g.hit) return h.onEdit(g.hit);
      if (!g.placing) return;
      const [px, py] = vp.convertToPdfPoint(g.x, g.y) as [number, number];
      return h.onNew({ page: g.page, shape: "pin", point: [num(px), num(py)] });
    }
    if (!g.placing) return drawPage(g.page);
    if (pre?.ref) {
      h.onMove(pre.ref, pre.dx ?? 0, pre.dy ?? 0);
      return;
    }
    if (!pre?.rect) return drawPage(g.page);
    const [sx0, sy0, sx1, sy1] = pre.rect;
    const a = vp.convertToPdfPoint(sx0, sy0) as [number, number];
    const b = vp.convertToPdfPoint(sx1, sy1) as [number, number];
    const rect: Rect = [num(Math.min(a[0], b[0])), num(Math.min(a[1], b[1])), num(Math.max(a[0], b[0])), num(Math.max(a[1], b[1]))];
    drawPage(g.page);
    h.onNew({ page: g.page, shape: "region", rects: [rect] });
  });
  container.addEventListener("pointercancel", (e) => {
    if (gesture?.pointerId === e.pointerId) cancel();
  });
  container.addEventListener("lostpointercapture", (e) => {
    if (gesture?.pointerId === e.pointerId) cancel();
  });

  // A finger tap on a mark opens it (finger taps elsewhere do nothing).
  container.addEventListener("click", (e) => {
    if ((e.target as Element).closest(".q2q-gutter")) return;
    // Pen and mouse are handled by the pointer handlers above; this is for finger taps.
    if (lastDownType === "pen" || lastDownType === "mouse") return;
    if (!lastDownType && performance.now() < suppressClickUntil) return;
    if (performance.now() - pinchEndedAt < 400) return;
    const ref = markAt(e.target);
    if (ref) {
      if (arranging.value) toggleArrange(ref);
      else h.onEdit(ref);
    }
  });

  // Touch: the Pencil must not scroll; two fingers pan and pinch-zoom.
  for (const target of [container, gutterRail]) target.addEventListener(
    "touchstart",
    (e) => {
      const touches = [...e.touches];
      if (touches.length === 1 && (touches[0] as Touch & { touchType?: string }).touchType === "stylus") {
        e.preventDefault();
        return;
      }
      if (touches.length === 2) {
        cancel();
        const [a, b] = touches as [Touch, Touch];
        pinch = {
          startDist: Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY),
          startScale: viewer.currentScale,
          cx: (a.clientX + b.clientX) / 2,
          cy: (a.clientY + b.clientY) / 2,
        };
        e.preventDefault();
      }
    },
    { passive: false },
  );
  for (const target of [container, gutterRail]) target.addEventListener(
    "touchmove",
    (e) => {
      if (!pinch || e.touches.length < 2) {
        if (e.touches.length === 1 && (e.touches[0] as Touch & { touchType?: string }).touchType === "stylus") e.preventDefault();
        return;
      }
      e.preventDefault();
      const [a, b] = [...e.touches] as [Touch, Touch];
      const dist = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      const cx = (a.clientX + b.clientX) / 2;
      const cy = (a.clientY + b.clientY) / 2;
      const pan: [number, number] = [cx - pinch.cx, cy - pinch.cy];
      pinch.cx = cx;
      pinch.cy = cy;
      const factor = (pinch.startScale * (dist / pinch.startDist)) / viewer.currentScale;
      if (Math.abs(factor - 1) > 0.02) viewer.updateScale({ drawingDelay: 400, scaleFactor: factor, origin: [cx, cy], pan });
      else if (pan[0] || pan[1]) container.scrollBy(-pan[0], -pan[1]);
    },
    { passive: false },
  );
  const endPinch = (e: TouchEvent) => {
    if (pinch && e.touches.length < 2) {
      pinch = null;
      pinchEndedAt = performance.now();
    }
  };
  for (const target of [container, gutterRail]) {
    target.addEventListener("touchend", endPinch);
    target.addEventListener("touchcancel", endPinch);
  }

  // Laptop: trackpad pinch (ctrl + wheel) zooms; plain wheel scrolls natively.
  for (const target of [container, gutterRail]) target.addEventListener(
    "wheel",
    (e) => {
      if (!e.ctrlKey) return;
      e.preventDefault();
      viewer.updateScale({ drawingDelay: 300, scaleFactor: Math.exp(-e.deltaY / 200), origin: [e.clientX, e.clientY] });
    },
    { passive: false },
  );
}
